import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';

import { useClearSidebarPanel, useOpenSidebarPanel } from '#/hooks/use-sidebar-panel-override';
import { useSubmenuCollapse } from '#/hooks/use-submenu-collapse';

import type { SidebarItem } from './types';

interface NavItemCollapsedProps {
  item: SidebarItem;
  onClick?: () => void;
  // This item's submenu panel is the one showing (Programs / Community).
  isPanelOpen?: boolean;
}

export const NavItemCollapsed = ({ item, onClick, isPanelOpen = false }: NavItemCollapsedProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { expandSubmenu } = useSubmenuCollapse();
  const openPanel = useOpenSidebarPanel();
  const clearPanel = useClearSidebarPanel();
  const hasChildren = item.children && item.children.length > 0;
  // A menu with a panel is selected while its panel shows — Community has no
  // route of its own, and Programs stays selected on every program page. Plain
  // links (Dashboard) are selected on their own page.
  const isActive = hasChildren ? isPanelOpen : item.path ? location.pathname === item.path : false;
  const Icon = item.icon;

  // Items with children open the submenu panel. One with a path (Programs) also
  // navigates there; one without (Community) just shows its panel on this page,
  // with every folder expanded.
  const handleClick = () => {
    expandSubmenu();
    if (item.path) {
      clearPanel();
      navigate({ to: item.path });
    } else {
      openPanel(item.id, location.pathname);
    }
    onClick?.();
  };

  // Icon-only items (the sidebar toggle) render as an outlined square button
  // without a caption, centred in the rail.
  const content = item.iconOnly ? (
    <Tooltip title={item.label} placement="right" arrow>
      <Box
        aria-label={item.label}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          p: '3px',
          cursor: 'pointer',
          color: 'grey.700',
          borderRadius: 1,
          transition: 'background-color 0.2s ease-in-out, transform 0.2s ease-in-out',
          '&:active': { transform: 'scale(0.92)' },
        }}
      >
        {/* Keyed by label so the expand/collapse glyph swap remounts and
            replays the fade-in instead of switching instantly. */}
        <Box
          key={item.label}
          sx={{
            display: 'flex',
            animation: 'sidebar-toggle-icon-in 0.2s ease-in-out',
            '@keyframes sidebar-toggle-icon-in': {
              from: { opacity: 0, transform: 'scale(0.7)' },
              to: { opacity: 1, transform: 'scale(1)' },
            },
          }}
        >
          <Icon width={24} height={24} size={24} />
        </Box>
      </Box>
    </Tooltip>
  ) : (
    <Tooltip title={item.label} placement="right" arrow>
      <Box
        onClick={hasChildren ? handleClick : onClick}
        sx={{
          gap: 0.4,
          p: '3px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: theme => (isActive ? theme.palette.grey[900] : theme.palette.grey[500]),
          borderRadius: 1,
          '&:hover': {
            bgcolor: theme => theme.palette.grey[200],
          },
        }}
      >
        <Icon width={24} height={24} size={24} />
        <Typography
          variant="caption"
          sx={{ fontSize: '9px', lineHeight: 1.3, mt: 0.25, fontWeight: isActive ? 700 : 400 }}
        >
          {item.label}
        </Typography>
      </Box>
    </Tooltip>
  );

  // Items with onClick handler
  if (item.onClick && !hasChildren) {
    return (
      <Box onClick={item.onClick} sx={{ cursor: 'pointer', width: '100%', textAlign: 'center' }}>
        {content}
      </Box>
    );
  }

  // Items with external URL open in a new tab
  if (item.externalUrl && !hasChildren) {
    return (
      <a
        href={item.externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'none', color: 'inherit', width: '100%', textAlign: 'center' }}
      >
        {content}
      </a>
    );
  }

  // Items with path but NO children use Link for navigation
  if (item.path && !hasChildren) {
    return (
      <Link
        to={item.path}
        // Re-clicking the current page (Dashboard on /) keeps the pathname, so
        // drop any panel opened from the rail explicitly.
        onClick={clearPanel}
        style={{ textDecoration: 'none', color: 'inherit', width: '100%', textAlign: 'center' }}
      >
        {content}
      </Link>
    );
  }

  // Items with children (with or without path) handle click via handleClick
  return content;
};
